import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const ProductShowcaseForm = ({ sectionId, itemToEdit, defaultCategory, onSuccess, onCancel }) => {
  const isEdit = Boolean(itemToEdit);

  const [formData, setFormData] = useState({
    title: '',
    title_color: '#ffffff', // Cor padrão branca para dar leitura no banner
    description: '', 
    description_color: '#ffffff', // Cor padrão branca
    category: defaultCategory || 'galeria_horizontal',
    icon_class: '', 
    item_order: 1,
    is_intro: false 
  });

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit && itemToEdit) {
      setFormData({
        title: itemToEdit.title || '',
        title_color: itemToEdit.title_color || '#ffffff',
        description: itemToEdit.description || '', 
        description_color: itemToEdit.description_color || '#ffffff',
        category: itemToEdit.category || defaultCategory || 'galeria_horizontal',
        icon_class: itemToEdit.icon_class || '',
        item_order: itemToEdit.item_order || 1,
        is_intro: itemToEdit.is_intro || false
      });
    }
  }, [isEdit, itemToEdit, defaultCategory]);

  // Desempacota as cores se for editar um banner
  useEffect(() => {
    if (isEdit && itemToEdit && itemToEdit.category === 'banner_principal' && itemToEdit.icon_class) {
      const cores = itemToEdit.icon_class.split('|');
      if (cores.length === 2) {
        setFormData(prev => ({
          ...prev,
          title_color: cores[0],
          description_color: cores[1]
        }));
      }
    }
  }, [isEdit, itemToEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sectionId) return alert("Erro: ID da seção indefinido.");
    
    if (!formData.is_intro && !file && !isEdit) {
        return alert("Você precisa anexar uma imagem ou ícone (PNG) para este item.");
    }

    setLoading(true);
    const data = new FormData();
    data.append('section_id', sectionId);
    data.append('title', formData.title);
    data.append('description', formData.description);
    data.append('category', formData.category);
    data.append('item_order', formData.item_order);
    data.append('is_intro', formData.is_intro); 
    
    // TÉCNICA "SEM BANCO": Salva as cores dentro da coluna icon_class
    if (formData.category === 'banner_principal') {
      const colorPayload = `${formData.title_color}|${formData.description_color}`;
      data.append('icon_class', colorPayload);
    } else {
      data.append('icon_class', formData.icon_class);
    }
    
    if (file) data.append('carousel_images', file);

    try {
      if (isEdit) {
        await api.put(`/admin/items/${itemToEdit.id}`, data);
      } else {
        await api.post(`/admin/sections/${sectionId}/items`, data);
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar item.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate__animated animate__fadeIn border p-4 bg-white rounded shadow-sm">
      <div className="d-flex justify-content-between mb-4 border-bottom pb-3">
        <h5 className="fw-bold text-primary m-0">
          <i className={`bi ${isEdit ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`}></i>
          {isEdit ? 'Editar Item' : 'Novo Item'}
        </h5>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}>
          <i className="bi bi-x-lg"></i> Cancelar
        </button>
      </div>
      
      <form onSubmit={handleSubmit}>
        
        {/* BLOCO 1: Configurações Básicas */}
        <div className="row mb-3">
          <div className="col-md-6">
            <label className="form-label fw-bold small">ALVO / CATEGORIA</label>
            <select name="category" className="form-select bg-light" value={formData.category} onChange={handleChange}>
              <option value="banner_principal">TOPO: Banner Principal da Seção</option>
              <option value="galeria_horizontal">BLOCO 1: Galeria Horizontal (1:1)</option>
              <option value="amenidade">BLOCO 2: Amenidades (Ícones PNG)</option>
              <option value="galeria_vertical">BLOCO 3: Galeria Vertical (9:16)</option>
              <option value="planta">BLOCO 4: Planta Humanizada (Abas)</option>
              <option value="destaque">BLOCO 5: Destaques Finais (PNG)</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label fw-bold small">ORDEM DE EXIBIÇÃO</label>
            <input type="number" name="item_order" className="form-control" value={formData.item_order} onChange={handleChange} min="1" required />
          </div>
        </div>

        {/* BLOCO 2: É Título do Bloco? */}
        {formData.category !== 'banner_principal' && (
          <div className="mb-4 p-3 bg-light border rounded">
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" name="is_intro" id="isIntroCheck" checked={formData.is_intro} onChange={handleChange} />
              <label className="form-check-label fw-bold text-dark" htmlFor="isIntroCheck">
                Este item é o TÍTULO/INTRODUÇÃO deste bloco?
              </label>
            </div>
            <small className="text-muted d-block mt-1">Marque isso para cadastrar o título da galeria, das plantas, etc. Se marcado, não é necessário enviar imagem.</small>
          </div>
        )}

        {/* BLOCO 3: Textos e Cores */}
        <div className="row mb-3">
          <div className="col-md-9">
            <label className="form-label fw-bold small">
              {formData.category === 'planta' && !formData.is_intro ? 'NOME DA ABA (Ex: 2 Suítes)' : 'TÍTULO'}
            </label>
            <input type="text" name="title" className="form-control" value={formData.title} onChange={handleChange} required />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold small text-primary">COR DO TÍTULO</label>
            <input type="color" name="title_color" className="form-control form-control-color w-100" value={formData.title_color} onChange={handleChange} title="Escolha a cor da fonte do título" />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-9">
            <label className="form-label fw-bold small">
              {formData.is_intro ? 'SUBTÍTULO / TEXTO DE APOIO' : 'DESCRIÇÃO CURTA'}
            </label>
            <textarea name="description" className="form-control" rows="2" value={formData.description} onChange={handleChange}></textarea>
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold small text-primary">COR DA DESCRIÇÃO</label>
            <input type="color" name="description_color" className="form-control form-control-color w-100" value={formData.description_color} onChange={handleChange} title="Escolha a cor da fonte da descrição" />
          </div>
        </div>

        {/* BLOCO 4 - Imagem Universal */}
        {!formData.is_intro && (
          <div className="mb-4">
            <div className="p-3 bg-light border rounded">
              <label className="form-label fw-bold small">
                {formData.category === 'destaque' ? 'ÍCONE PNG DE DESTAQUE (Fundo Transparente)' : 
                 formData.category === 'amenidade' ? 'ÍCONE PNG DA AMENIDADE (Fundo Transparente)' : 
                 formData.category === 'banner_principal' ? 'IMAGEM DO BANNER (Horizontal)' : 'IMAGEM DO ITEM'}
              </label>
              <input type="file" className="form-control" accept="image/*" onChange={handleFileChange} />
              {isEdit && <small className="text-warning mt-1 d-block"><i className="bi bi-exclamation-triangle"></i> Enviar uma nova imagem/ícone substituirá o atual.</small>}
            </div>
          </div>
        )}

        {/* BOTÕES DE AÇÃO */}
        <div className="d-flex gap-2 pt-2 border-top">
          <button type="submit" className="btn btn-dark px-5 fw-bold" disabled={loading}>
            {loading ? 'Salvando...' : 'SALVAR ITEM'}
          </button>
        </div>

      </form>
    </div>
  );
};

export default ProductShowcaseForm;